import { EventType } from "@/analytics/analytics-events";
import { updateProject } from "@/components/common/project-provider/update-project";
import { DeleteElementDialogContent } from "@/components/ui/delete-element-dialog-content";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { changeMode } from "@/store/mode-slice";
import { selectActiveArea, selectAreaFor } from "@/store/selections-selectors";
import { setActiveElement } from "@/store/selections-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import {
  DeleteIcon,
  FaroMenuItem,
  selectIElement,
  TranslateVar,
  useDialog,
} from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  createMutationDeleteVideoModeData,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { useCallback, type JSX } from "react";
import { ContextMenuAction, ContextMenuActionType } from "../action-types";

export const DELETE_VIDEO_RECORDING_ACTION: ContextMenuAction = {
  type: ContextMenuActionType.deleteVideoRecording,
  CustomComponent: DeleteVideoRecordingAction,
};

type DeleteVideoRecordingActionProps = {
  /** The id of the element to delete */
  elementId: string;

  /** Whether to use dark mode styling */
  dark?: boolean;

  /** Callback to close the context menu after the action is performed */
  closeContextMenu(): void;
};

/**
 * @returns A menu item that opens a confirmation dialog to delete a video recording when clicked
 */
export function DeleteVideoRecordingAction({
  elementId,
  dark,
  closeContextMenu,
}: DeleteVideoRecordingActionProps): JSX.Element | null {
  const dispatch = useAppDispatch();
  const { createDialog } = useDialog();
  const { projectApiClient } = useApiClientContext();
  const { handleErrorWithDialog } = useErrorHandlers();

  const videoRecordings = useAppSelector(selectIElement(elementId));
  const area = useAppSelector(selectAreaFor(videoRecordings));
  const activeArea = useAppSelector(selectActiveArea);

  const onConfirmDialog = useCallback(async () => {
    try {
      if (!videoRecordings) return false;

      Analytics.track(EventType.deleteVideoRecording);

      await projectApiClient.applyMutations([
        createMutationDeleteVideoModeData(elementId, true),
      ]);

      if (videoRecordings.parentId) {
        await dispatch(
          updateProject({
            projectApi: projectApiClient,
            iElementQuery: {
              // if possible, update the entire area, as the mutation also deletes the Img360s
              ancestorIds: [area?.id ?? videoRecordings.parentId],
            },
          }),
        );
      }

      // reset to to a state that is known to not rely on deleted elements.
      // The sheet is the only mode that can render any project, while the overview needs
      // a point cloud or a CAD model and crashes without them.
      dispatch(setActiveElement(activeArea?.id ?? videoRecordings.rootId));
      dispatch(changeMode("sheet"));

      return true;
    } catch (error) {
      handleErrorWithDialog({
        title: "Failed to delete video recording",
        error,
      });
      return false;
    } finally {
      closeContextMenu();
    }
  }, [
    videoRecordings,
    projectApiClient,
    elementId,
    dispatch,
    activeArea?.id,
    area?.id,
    handleErrorWithDialog,
    closeContextMenu,
  ]);

  const deleteVideoRecording = useCallback(async () => {
    if (!videoRecordings) return;

    // Create a delete confirmation dialog
    await createDialog({
      title: "Delete Video Recording?",
      confirmText: "Delete",
      variant: "danger",
      content: (
        <DeleteElementDialogContent
          name={
            <TranslateVar name="elementName">
              {videoRecordings.name}
            </TranslateVar>
          }
          extra="This will also delete all images generated from this video recording."
        />
      ),
      onConfirm: onConfirmDialog,
    });
  }, [createDialog, onConfirmDialog, videoRecordings]);

  return (
    <FaroMenuItem
      Icon={DeleteIcon}
      dark={dark}
      label="Delete"
      onClick={deleteVideoRecording}
    />
  );
}
